import { AsyncPipe, NgClass, NgFor, NgIf, NgTemplateOutlet } from "@angular/common";
import { Component, inject, Injector, OnDestroy, OnInit, signal } from "@angular/core";
import { AngularFireAnalytics } from "@angular/fire/compat/analytics";
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from "@angular/material/expansion";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatTab, MatTabGroup } from "@angular/material/tabs";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { ITimer } from "@interfaces/timer";
import { IonicModule, MenuController, NavController } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Voucher } from "@interfaces/vouchers.i";
import { LottieComponent } from "ngx-lottie";
import { BehaviorSubject, of, Subject, Subscription } from "rxjs";
import { catchError, map, takeUntil } from "rxjs/operators";
import { bounceIn } from "src/app/animations/custom.animation";
import { BaseService } from "src/app/app.base.service";
import { CommonVariable } from "src/app/app.constants";
import { HomeDataService } from "src/app/services/home-data.service";
import { VoucherService } from "src/app/services/vouchers.service";
import { VoucherTimerService } from "./voucher-timer.service";
import { VoucherNotificationService } from "./voucher-notification.service";
import { StorePaymentFacade } from "./store-payment.facade";
import { CountDownTimerAppComponent } from "../count-down-timer-app/count-down-timer-app.component";
import { CountDownTimerComponent } from "../count-down-timer/count-down-timer.component";
import { AddHomeScreenBannerComponent } from "../add-home-screen-banner/add-home-screen-banner.component";
import { PushNotificationBannerComponent } from "../push-notification-banner/push-notification-banner.component";
import { VoucherRedemptionService } from "./voucher-redemption.service";
import type { SwiperOptions } from "swiper/types";
import { SwiperModule } from "swiper/angular";
import { ClarityService } from "src/app/services/clarity.service";

@Component({
  selector: "app-vouchers",
  templateUrl: "./vouchers.component.html",
  styleUrls: ["./vouchers.component.scss"],
  animations: [bounceIn],
  standalone: true,
  providers: [VoucherRedemptionService, VoucherTimerService, VoucherNotificationService, StorePaymentFacade],
  imports: [
    IonicModule,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    NgClass,
    NgIf,
    MatProgressSpinner,
    MatIcon,
    MatTabGroup,
    MatTab,
    NgTemplateOutlet,
    PushNotificationBannerComponent,
    AddHomeScreenBannerComponent,
    NgFor,
    CountDownTimerComponent,
    LottieComponent,
    AsyncPipe,
    TranslatePipe,
    CountDownTimerAppComponent,
    SwiperModule,
  ],
})
export class VouchersComponent extends BaseService implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();
  private awardedVoucherListSubject$ = new BehaviorSubject<Voucher[] | null>(null);
  private upcomingVoucherListSubject$ = new BehaviorSubject<Voucher[] | null>(null);
  translatedToastMsg: string | Data;
  isOnline: boolean
  public userId: string;
  isVoucherDisableSignal = signal<boolean>(false);
  private clarityService = inject(ClarityService);

  get countdownSeconds(): number | null {
    return this.voucherNotificationService.countdownSeconds;
  }

  get isVoucherClaimedSignal() {
    return this.voucherNotificationService.isVoucherClaimedSignal;
  }

  get isVoucherIsInvalid$() {
    return this.voucherRedemptionService.isVoucherIsInvalid$;
  }

  get isVoucherCodeInvalid(): boolean {
    return this.voucherRedemptionService.isVoucherCodeInvalid;
  }

  get isButtonDisabled(): boolean {
    return this.voucherRedemptionService.isButtonDisabled;
  }

  get spinnerAnimation(): boolean {
    return this.voucherRedemptionService.spinnerAnimation;
  }

  get progressBar(): number {
    return this.voucherRedemptionService.progressBar;
  }

  get proceed(): boolean {
    return this.voucherRedemptionService.proceed;
  }

  get claimSuccess(): boolean {
    return this.voucherRedemptionService.claimSuccess;
  }

  get claimFromList(): boolean {
    return this.voucherRedemptionService.claimFromList;
  }

  awardedVoucherList$ = this.awardedVoucherListSubject$.asObservable();
  /** Upcoming vouchers; live Firestore updates preserved (F-3) — do not reassign this reference. */
  voucherList$ = this.upcomingVoucherListSubject$.asObservable();
  errorMessage = "";

  selectedLanguage: string = "en";
  vc: string;
  vouchers: any = [];
  showPartnerOffers: boolean;
  partnerOfferIframeLink: Data;
  globalPartnerOffersSubscription: Subscription;
  checklistVoucherStatus: boolean;
  languageCode: string = CommonVariable.preferredLanguage;

  get inlineNotificationDetails(): Data {
    return this.voucherNotificationService.inlineNotificationDetails;
  }

  get $timer(): ITimer[] {
    return this.voucherTimerService.$timer;
  }

  get voucherStartTime(): number {
    return this.voucherTimerService.voucherStartTime;
  }

  get storeDiscountDetails(): Data {
    return this.storePaymentFacade.storeDiscountDetails;
  }
  // Swiper options for the store slider
  storeSliderOptions: SwiperOptions = {
    slidesPerView: 1.2,
    spaceBetween: 10,
    centeredSlides: true,
    pagination: {
      clickable: true,
      dynamicBullets: true, // always show dots
    },
    slideToClickedSlide: true,
  };

  constructor(
    private navCtrl: NavController,
    private voucherService: VoucherService,
    public voucherRedemptionService: VoucherRedemptionService,
    private voucherTimerService: VoucherTimerService,
    public voucherNotificationService: VoucherNotificationService,
    private storePaymentFacade: StorePaymentFacade,
    injector: Injector,
    private analytics: AngularFireAnalytics,
    private router: Router,
    private menu: MenuController,
    public translate: TranslateService,
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private homeDataService: HomeDataService) {
    super(injector);
    try {
      this.authService?.selectedlanguage$
        ?.pipe(takeUntil(this.unsubscribe$))
        .subscribe((l) => {
          this.selectedLanguage = l ? l : "en";
          if (l) {
            const selectedLanguage = l ? l : "en";
            const finalLanguage = CommonVariable.bypassLanguages.includes(selectedLanguage) ? "en" : selectedLanguage;
            this.selectedLanguage = finalLanguage;
            translate.use(finalLanguage);
          }
        });
      this.authService?.getConstantsMsg$
        ?.pipe(takeUntil(this.unsubscribe$))
        .subscribe((msg: Data) => {
          this.translatedToastMsg = msg?.static_messages_pwa;
        });
    } catch (e) {}

    //This subscription is to fetch the status of Redeem voucher event from new_member_checklist home card
    this.homeDataService?.homeCardsVM$
      .pipe(
        takeUntil(this.unsubscribe$),
        catchError((err) => {
          this.authService.errorHandlerLogs("Error in homeCardsVM$ subscription (vouchers):", err);
          return of(null);
        })
      )
      .subscribe(
      (res: Data) => {
        if (res && res.hasOwnProperty("new_member_checklist") && 
            res.new_member_checklist?.content?.checklist &&
            Array.isArray(res.new_member_checklist.content.checklist)) {
          const digitOne = 1;
          const checklist = res.new_member_checklist.content.checklist;
          if (checklist.length > digitOne) {
            this.checklistVoucherStatus = checklist[digitOne]?.status;
          }
        }
      },
      (error) => {
        this.authService.errorHandlerLogs('Subscription error (vouchers):', error);
      }
    );
  }

  async ngOnInit() {
    await this.authService.getUid("VouchersComponent").then((uid) => {
      this.userId = uid;
    })

    this.analytics.logEvent("voucher_screen", {
      userId: this.userId,
    });
    try {
      this.activatedRoute?.queryParams
        ?.pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (params: Data) => {
            this.voucherRedemptionService.callFromPromotion = params?.call_from;
            this.vc = params?.vc;
          },
          (err) => {
            this.authService.errorHandlerLogs("VouchersComponent queryParams", err);
          }
        );
    } catch (e) {
      return e;
    }

    this.initVoucherListStreams();

    try {
      this.authService.readAllVouchers();
      this.getVoucherDetails();
    } catch (error) {
      return error;
    }
  }

  ngOnDestroy() {
    this.voucherTimerService.destroy();
    this.globalPartnerOffersSubscription?.unsubscribe();
    this.voucherRedemptionService.destroy();
    this.voucherNotificationService.destroy();
    this.storePaymentFacade.destroy();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ionViewWillEnter() {
    this.voucherRedemptionService.isVoucherIsInvalid$.next(false);
    this.authService?.userDetails$
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (userData: Data) => {
          this.languageCode = userData?.language_code || CommonVariable.preferredLanguage;
          this.getPartnerOffers(userData);
        },
        (err) => {
          this.authService.errorHandlerLogs("VouchersComponent ionViewWillEnter userDetails$", err);
        }
      );
  }

  getPartnerOffers(user): void {
    try {
      if (user && user?.partner) {
        this.globalPartnerOffersSubscription?.unsubscribe();
        this.globalPartnerOffersSubscription = this.authService
          ?.getGlobalPartnerOffers(user?.partner)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(
            (res) => {
              if (res && res?.status) {
                this.showPartnerOffers = true;
                this.partnerOfferIframeLink = this.sanitizer?.bypassSecurityTrustResourceUrl(res?.iframe_url);
              } else {
                this.showPartnerOffers = false;
              }
            },
            () => {
              this.showPartnerOffers = false;
            }
          );
      } else {
        this.showPartnerOffers = false;
      }
    } catch (error) {
      this.showPartnerOffers = false;
    }
  }

  goBack() {
    this.navCtrl.back();
  }
  cleanInputdata(inputString: any) {
    return this.voucherRedemptionService.cleanInputdata(inputString);
  }

  handleDiscountVoucherRedirection(voucherData: Data) {
    return this.voucherRedemptionService.handleDiscountVoucherRedirection(voucherData);
  }

  startSpinner() {
    return this.voucherRedemptionService.startSpinner();
  }

  getButtonStatus(voucherCode: string) {
    return this.voucherRedemptionService.getButtonStatus(voucherCode);
  }

  redeemVoucher(voucherCode: string, claimFromList: boolean, voucherObj?: Voucher) {
    this.clarityService.event("redeem_voucher");
    return this.voucherRedemptionService.redeemVoucher(voucherCode, claimFromList, voucherObj);
  }

  getIconUrl(iconUrl: string | undefined): string {
    return this.authService.getIconUrl(iconUrl);
  }

  getVoucherDetails(): void {
    this.voucherNotificationService.getVoucherDetails();
  }

  validateNotifications(syndicateDetails: Data): void {
    this.voucherNotificationService.validateNotifications(syndicateDetails);
  }

  handleInlineNotification(notificationDetails: Data) {
    this.voucherNotificationService.handleInlineNotification(notificationDetails, this.languageCode);
  }

  isPushNotification(): boolean {
    return this.voucherNotificationService.isPushNotification();
  }

  isAddHomeScreen(): boolean {
    return this.voucherNotificationService.isAddHomeScreen();
  }

  getNotificationDetails(syndicateDetails: Data) {
    this.voucherNotificationService.getNotificationDetails(syndicateDetails);
  }

  feedBackDismiss(): void {
    this.voucherNotificationService.feedBackDismiss();
  }

  getRemainingExpiryTime(): void {
    this.voucherTimerService.getRemainingExpiryTime();
  }

sortVouchers(voucherList: Voucher[] | null | undefined): Voucher[] {
    if (voucherList == null || !Array.isArray(voucherList)) {
      return [];
    }
    return this.voucherTimerService.sortVouchers(voucherList);
  }
  getRemainingTime(event: number, voucher: Voucher, val: number): void {
    const awarded = this.awardedVoucherListSubject$.getValue();
    const upcoming = this.upcomingVoucherListSubject$.getValue();
    if (awarded?.some((v) => v?.id === voucher?.id)) {
      const next = this.voucherTimerService.getRemainingTimeSnapshot(awarded, event, voucher, val);
      this.awardedVoucherListSubject$.next(next);
      return;
    }
    if (upcoming?.some((v) => v?.id === voucher?.id)) {
      const next = this.voucherTimerService.getRemainingTimeSnapshot(upcoming, event, voucher, val);
      this.upcomingVoucherListSubject$.next(next);
    }
  }

  private initVoucherListStreams(): void {
    this.voucherService
      .getVoucherList()
      .pipe(
        map((vList: Voucher[] | null | undefined) => ({
          awarded: this.voucherTimerService.buildAwardedVouchers(vList),
          upcoming: this.voucherTimerService.buildUpcomingVouchers(vList),
        })),
        catchError(() => of({ awarded: [], upcoming: [] })),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(({ awarded, upcoming }) => {
        this.getCountDown(awarded);
        this.awardedVoucherListSubject$.next(awarded);
        this.upcomingVoucherListSubject$.next(upcoming);
      });
  }

  /** Voucher list expiration time calculation for every second */
  getCountDown(voucherDetails: Voucher[]): void {
    this.voucherTimerService.getCountDown(voucherDetails);
  }

  async showInbox() {
    await this.menu.open("inboxMenu").catch(() => { });
  }
  navigateToStore() {
    this.router.navigateByUrl("mainTabs/tabs/store");
  }

  handleCountdownFinish(): void {
    this.voucherNotificationService.handleCountdownFinish();
  }

  trackVoucher(index: number, voucher: Voucher) {
    return voucher?.id || index;
  }

  clickOfferBanner(): void {
    this.storePaymentFacade.clickOfferBanner();
  }

  storePaymentPage(data: Data): void {
    this.storePaymentFacade.storePaymentPage(data);
  }

  showTicket(syndicateInfo: Data, paymentModal: HTMLIonModalElement): void {
    this.storePaymentFacade.showTicket(syndicateInfo, paymentModal);
  }

  trackCTAClick(body?: Data): void {
    this.storePaymentFacade.trackCTAClick(body);
  }
}
